import { useEffect, useRef } from 'react'
import type { CloudMovieRecord } from '@govu/shared'
import type { DeviceSummary } from '../devices/useDevices'
import { LazyImage } from '../ui/LazyImage'
import { useTmdbLazyMovie } from './useTmdbLazy'

interface Props {
  tmdbId: number
  record: CloudMovieRecord
  devices: Map<string, DeviceSummary>
  onOpen: (tmdbId: number, type: 'movie') => void
  /**
   * Grid keyboard navigation focus — when true the card renders a
   * visible focus ring and pulls real DOM focus onto its button so
   * tab order / screen readers stay in sync with the logical focus.
   */
  focused?: boolean
  onFocus?: () => void
}

/**
 * Movie card used inside the PWA library grid. Historically this was a
 * thin wrapper around `@govu/shared/MovieCardView`, but we now render
 * the chrome locally so we can:
 *
 *   - Use the PWA's `<LazyImage>` primitive for posters instead of a
 *     raw `<img>`, matching the desktop renderer's lazy-load behaviour.
 *   - Expose a `focused` prop for grid keyboard navigation with a
 *     visible ring, mirroring `DiscoverCard`.
 *
 * The visual layout (gold year, gold user rating badge, blue TMDB
 * rating fallback, gradient title overlay) is kept identical to the
 * shared `MovieCardView` so users don't notice the swap.
 */
export function MovieCard({
  tmdbId,
  record,
  devices,
  onOpen,
  focused = false,
  onFocus,
}: Props) {
  const { details: tmdb, ref } = useTmdbLazyMovie(tmdbId)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const isVirtual = record.isVirtual !== false
  const ownerNames = (record.ownerDeviceIds ?? [])
    .map((id) => devices.get(id)?.name)
    .filter((n): n is string => !!n)

  // Defensive coercion: Firestore can occasionally surface a number-shaped
  // rating as a string (legacy backfill paths, hand-edited docs). Coerce
  // through Number() so we don't drop a valid rating just because its JS
  // type is off — only treat NaN / non-positive values as "unrated".
  const userRating = coerceRating(record.userRating)
  const tmdbRating = coerceRating(tmdb?.rating) ?? 0

  const title = tmdb?.title ?? `TMDB #${tmdbId}`
  const year = tmdb?.year ?? null
  const poster = tmdb?.posterUrl ?? null
  const loading = !tmdb

  useEffect(() => {
    if (focused && buttonRef.current && document.activeElement !== buttonRef.current) {
      buttonRef.current.focus({ preventScroll: true })
    }
  }, [focused])

  return (
    <div ref={ref as (el: HTMLDivElement | null) => void} className="group flex flex-col">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => onOpen(tmdbId, 'movie')}
        onFocus={onFocus}
        data-focused={focused || undefined}
        className={`relative block aspect-[2/3] w-full overflow-hidden rounded-lg border bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic transition-all duration-300 ease-cinematic focus:outline-none ${
          focused
            ? 'border-accent-gold ring-2 ring-accent-gold scale-[1.03] shadow-cinematic-lg'
            : 'border-border-primary group-hover:scale-[1.03] group-hover:shadow-cinematic-lg'
        }`}
        aria-label={
          year != null
            ? `${title} (${year})${tmdbRating > 0 ? `, TMDB rating ${tmdbRating.toFixed(1)}` : ''}`
            : title
        }
      >
        {poster ? (
          <LazyImage
            src={poster}
            alt={title}
            className="h-full w-full"
            placeholderColor="#1a1a1a"
          />
        ) : (
          <PlaceholderPoster label={title} loading={loading} />
        )}

        {userRating != null ? (
          <Badge className="bg-accent-gold/80 text-black">
            <Star /> {userRating}
          </Badge>
        ) : tmdbRating > 0 ? (
          <Badge className="bg-blue-500/80 text-white">
            <Star /> {tmdbRating.toFixed(1)}
          </Badge>
        ) : null}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-2 pt-8">
          <p className="line-clamp-2 text-sm font-medium leading-tight text-white drop-shadow-lg">
            {title}
          </p>
          <div className="mt-0.5 flex items-center gap-2 text-[11px]">
            {year && <span className="font-medium text-accent-gold drop-shadow">{year}</span>}
            {!isVirtual && (
              <span className="truncate text-white/80 drop-shadow">
                {ownerNames.length > 0 ? `On ${ownerNames.join(', ')}` : 'On another device'}
              </span>
            )}
          </div>
        </div>
      </button>
    </div>
  )
}

function Badge({ children, className }: { children: React.ReactNode; className: string }) {
  return (
    <div
      className={`absolute left-2 top-2 flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold shadow-sm backdrop-blur-sm ${className}`}
    >
      {children}
    </div>
  )
}

function Star() {
  return <span className="opacity-75">★</span>
}

/**
 * Normalize a rating value coming from Firestore or TMDB into a positive
 * finite number, or null when the value is missing / unusable. Accepts
 * both numeric and string inputs — Firestore docs occasionally store
 * ratings as strings (e.g. older import paths) and we don't want a stale
 * type to silently hide a real rating from the user.
 */
function coerceRating(value: unknown): number | null {
  if (value == null) return null
  const num = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(num) || num <= 0) return null
  return num
}

function PlaceholderPoster({ label, loading }: { label: string; loading: boolean }) {
  return (
    <div className="grid h-full w-full place-items-center px-2 text-center">
      <div className="space-y-2">
        {loading ? (
          <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-text-tertiary border-t-accent-primary" />
        ) : (
          <div className="mx-auto text-3xl opacity-60">🎬</div>
        )}
        <span className="line-clamp-2 text-xs text-text-tertiary">{label}</span>
      </div>
    </div>
  )
}
